import type { WorkshopJob } from "../../types/garage/atelier";
import { ATELIER_SNAP_HOUR, HOURS_END, HOURS_START } from "./gridTypes";

/** Un créneau sur la grille : début et durée en heures décimales. */
export interface HourRange {
  startHour: number;
  durationHours: number;
}

const round = (h: number) => +h.toFixed(4);

/**
 * Travail du même ordre, sur le même compagnon et le même jour, que le créneau
 * touche : chevauchement ou bord à bord (à un quart d'heure près). C'est la cible
 * d'une fusion — deux magnets du même ordre qui se touchent n'en font qu'un.
 * Un chevauchement l'emporte sur un simple contact.
 */
export function findSameOrdreTouching<J extends Pick<WorkshopJob, "id" | "workerId" | "date" | "startHour" | "durationHours" | "ordre_id">>(
  jobs: J[],
  workerId: string,
  date: string,
  startHour: number,
  durationHours: number,
  ordreId: string,
  excludeId?: string,
): J | null {
  const end = startHour + durationHours;
  let touching: J | null = null;
  for (const j of jobs) {
    if (j.id === excludeId || j.workerId !== workerId || j.date !== date || j.ordre_id !== ordreId) continue;
    const jEnd = j.startHour + j.durationHours;
    if (startHour < jEnd && end > j.startHour) return j;
    if (!touching && startHour < jEnd + ATELIER_SNAP_HOUR && end > j.startHour - ATELIER_SNAP_HOUR) touching = j;
  }
  return touching;
}

/**
 * Créneau issu de la fusion de `a` et `b` : du plus tôt des deux débuts à la plus
 * tardive des fins. Quand ils se chevauchent, aucune heure ne se perd : la durée
 * vaut au moins la somme des deux (lâcher 2 h à moitié sur un bloc de 3 h donne
 * 5 h, pas 4). Le bloc ne dépasse pas la fin de journée : il remonte d'abord, puis
 * s'arrête à `dayEnd` — `clippedHours` dit ce qui n'a pas tenu.
 */
export function mergedRange(
  a: HourRange,
  b: HourRange,
  dayEnd: number = HOURS_END,
  dayStart: number = HOURS_START,
): HourRange & { clippedHours: number } {
  const aEnd = a.startHour + a.durationHours;
  const bEnd = b.startHour + b.durationHours;
  let start = Math.min(a.startHour, b.startHour);
  const span = Math.max(aEnd, bEnd) - start;
  const overlap = a.startHour < bEnd && b.startHour < aEnd;
  const wanted = overlap ? Math.max(span, a.durationHours + b.durationHours) : span;
  let duration = wanted;
  if (start + duration > dayEnd) {
    start = Math.max(dayStart, dayEnd - duration);
    duration = Math.min(duration, dayEnd - start);
  }
  return {
    startHour: round(start),
    durationHours: round(duration),
    clippedHours: round(Math.max(0, wanted - duration)),
  };
}
